import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Copy, Ticket, Plus, KeyRound, X, Ban, Trash2, Pencil, Wallet } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { API, apiError } from "@/lib/api";

export default function AdminUsers() {
  const { t } = useI18n();
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState("");
  const [codes, setCodes] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [pwReqs, setPwReqs] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [pwResult, setPwResult] = useState(null); // {full_name, distributor_id, password}
  const [busyId, setBusyId] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [creditUser, setCreditUser] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount: "", reason: "" });

  useEffect(() => {
    API.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
    API.get("/admin/referral-codes").then(({ data }) => setCodes(data)).catch(() => {});
    API.get("/admin/registration-requests").then(({ data }) => setReqs(data)).catch(() => {});
    API.get("/admin/password-reset-requests").then(({ data }) => setPwReqs(data)).catch(() => {});
  }, []);

  const loadPwReqs = () => API.get("/admin/password-reset-requests").then(({ data }) => setPwReqs(data)).catch(() => {});
  const resetForReq = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await API.post(`/admin/users/${r.user_id}/reset-password`, {});
      setPwResult(data); setPwReqs((l) => l.filter((x) => x.id !== r.id));
      toast.success("New password generated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };
  const dismissPwReq = async (r) => {
    try { await API.post(`/admin/password-reset-requests/${r.id}/dismiss`); setPwReqs((l) => l.filter((x) => x.id !== r.id)); toast.success("Request dismissed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadReqs = () => API.get("/admin/registration-requests").then(({ data }) => setReqs(data)).catch(() => {});
  const approveReq = async (r) => { try { const { data } = await API.post(`/admin/registration-requests/${r.id}/approve`); toast.success(data.message); loadReqs(); API.get("/admin/users").then(({ data }) => setUsers(data)); } catch (e) { toast.error(apiError(e)); } };
  const rejectReq = async (r) => { const reason = window.prompt(`Reject ${r.full_name}? Optionally enter a reason:`, ""); if (reason === null) return; try { await API.post(`/admin/registration-requests/${r.id}/reject`, { reason }); toast.success("Request rejected"); loadReqs(); } catch (e) { toast.error(apiError(e)); } };

  const openEdit = (u) => { setEditForm({ full_name: u.full_name || "", email: u.email || "", mobile: u.mobile || "", address: u.address || "", state: u.state || "", city: u.city || "", pincode: u.pincode || "" }); setEditUser(u); };
  const saveEdit = async () => {
    setBusyId(editUser.id);
    try {
      const { data } = await API.put(`/admin/users/${editUser.id}`, editForm);
      setUsers((list) => list.map((x) => (x.id === editUser.id ? { ...x, ...data } : x)));
      toast.success("Profile updated");
      setEditUser(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const openCredit = (u) => { setCreditForm({ amount: "", reason: "" }); setCreditUser(u); };
  const submitCredit = async () => {
    const amt = parseFloat(creditForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount greater than 0"); return; }
    setBusyId(creditUser.id);
    try {
      const { data } = await API.post("/admin/wallet/credit", { distributor_id: creditUser.distributor_id, amount: amt, reason: creditForm.reason || "Manual admin credit" });
      toast.success(`${data.message} New balance ₹${data.available_balance}`);
      setCreditUser(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const generate = async () => {
    setGenLoading(true);
    try {
      const { data } = await API.post("/admin/referral-codes");
      setCodes((c) => [data, ...c]);
      toast.success(`Referral code ${data.code} generated`);
    } catch (e) { toast.error(apiError(e)); } finally { setGenLoading(false); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success(`Copied ${text}`); };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete reference code ${code}?`)) return;
    try {
      await API.delete(`/admin/referral-codes/${code}`);
      setCodes((cs) => cs.filter((x) => x.code !== code));
      toast.success("Reference code deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetPw = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await API.post(`/admin/users/${u.id}/reset-password`, {});
      setPwResult(data);
      toast.success("New password generated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const toggleBlock = async (u) => {
    const next = !u.blocked;
    if (!window.confirm(`${next ? "Block" : "Unblock"} ${u.full_name} (${u.distributor_id})?`)) return;
    setBusyId(u.id);
    try {
      await API.put(`/admin/users/${u.id}/block`, { blocked: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, blocked: next, account_status: next ? "blocked" : "active" } : x)));
      toast.success(next ? "User blocked" : "User unblocked");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently DELETE ${u.full_name} (${u.distributor_id})? This removes the user and their wallet and cannot be undone.`)) return;
    setBusyId(u.id);
    try {
      await API.delete(`/admin/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const filtered = useMemo(
    () => (users || []).filter((u) => !filter || u.full_name?.toLowerCase().includes(filter.toLowerCase()) || u.distributor_id?.toLowerCase().includes(filter.toLowerCase()) || u.email?.toLowerCase().includes(filter.toLowerCase())),
    [users, filter]
  );

  if (!users) return <Skeleton className="h-96 rounded-2xl" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold text-ink">{t("nav.users")}</h1>
        <input placeholder={t("common.search")} value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 w-64 rounded-full border border-brand-50 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand" data-testid="user-search" />
      </div>

      {pwResult && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold-light/50 p-4" data-testid="pw-reset-result">
          <div className="text-sm text-ink">
            New password for <b>{pwResult.full_name}</b> ({pwResult.distributor_id}):
            <button onClick={() => copy(pwResult.password)} className="ml-2 inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 font-mono font-semibold text-forest shadow-sm hover:shadow" data-testid="pw-reset-value">{pwResult.password} <Copy size={13} /></button>
            <span className="ml-2 text-xs text-muted2">Share this with the distributor. They can log in and change it.</span>
          </div>
          <button onClick={() => setPwResult(null)} className="text-muted2 hover:text-ink" data-testid="pw-reset-dismiss"><X size={18} /></button>
        </div>
      )}

      {/* Password reset requests */}
      {pwReqs.length > 0 && (
        <div className="rounded-2xl border border-gold/40 bg-gold-light/30 p-6 shadow-card" data-testid="password-reset-requests-card">
          <h3 className="font-display text-lg font-medium text-ink">Password Reset Requests</h3>
          <p className="text-xs text-muted2">Distributors who forgot their password. Reset it and share the new password with them (no OTP/email).</p>
          <div className="mt-4 space-y-2">
            {pwReqs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-brand-50 bg-white p-3" data-testid={`pw-req-${r.distributor_id}`}>
                <div className="text-sm"><span className="font-semibold text-forest">{r.distributor_id}</span> · <span className="text-ink">{r.full_name}</span> · <span className="font-mono text-muted2">{r.mobile}</span><span className="ml-2 text-xs text-muted2">{(r.created_at || "").slice(0, 10)}</span></div>
                <div className="flex gap-2">
                  <button onClick={() => resetForReq(r)} disabled={busyId === r.id} className="rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white hover:bg-brand disabled:opacity-50" data-testid={`pw-req-reset-${r.distributor_id}`}>{busyId === r.id ? "…" : "Reset Password"}</button>
                  <button onClick={() => dismissPwReq(r)} className="rounded-full border border-danger/40 px-3 py-1 text-xs font-semibold text-danger hover:bg-danger/10" data-testid={`pw-req-dismiss-${r.distributor_id}`}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Registration requests */}
      <div className="rounded-2xl border border-brand-50 bg-white p-6 shadow-card" data-testid="registration-requests-card">
        <h3 className="font-display text-lg font-medium text-ink">Registration Requests</h3>
        <p className="text-xs text-muted2">Approve to generate a Distributor ID (RED, active). No OTP — verify the applicant manually.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-brand-50 text-left text-muted2"><th className="p-3">Name</th><th className="p-3">Mobile</th><th className="p-3">Email</th><th className="p-3">Submitted</th><th className="p-3">Status</th><th className="p-3">Action</th></tr></thead>
            <tbody>
              {reqs.filter((r) => r.status === "pending").length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted2">No pending requests.</td></tr>}
              {reqs.filter((r) => r.status === "pending").map((r) => (
                <tr key={r.id} className="border-b border-brand-50/60" data-testid={`reg-req-${r.mobile}`}>
                  <td className="p-3 text-ink">{r.full_name}</td>
                  <td className="p-3 font-mono text-muted2">{r.mobile}</td>
                  <td className="p-3 text-muted2">{r.email || "—"}</td>
                  <td className="p-3 text-muted2">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="p-3"><span className="rounded-full bg-gold-light px-2 py-0.5 text-xs font-semibold text-gold">pending</span></td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => approveReq(r)} className="rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white hover:bg-brand" data-testid={`approve-${r.mobile}`}>Approve</button>
                      <button onClick={() => rejectReq(r)} className="rounded-full border border-danger/40 px-3 py-1 text-xs font-semibold text-danger hover:bg-danger/10" data-testid={`reject-${r.mobile}`}>Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Referral code generator */}
      <div className="rounded-2xl border border-brand-50 bg-white p-6 shadow-card" data-testid="referral-code-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-forest"><Ticket size={20} /></div>
            <div>
              <h3 className="font-display text-lg font-medium text-ink">Referral Codes</h3>
              <p className="text-xs text-muted2">Generate a code and share it to onboard new distributors (referral is mandatory at signup).</p>
            </div>
          </div>
          <Button onClick={generate} disabled={genLoading} className="rounded-full bg-forest hover:bg-brand" data-testid="generate-referral-code-btn">
            <Plus size={16} className="mr-1.5" /> {genLoading ? "Generating…" : "Generate Code"}
          </Button>
        </div>
        {codes.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {codes.slice(0, 12).map((c) => (
              <div key={c.code} className="group inline-flex items-center gap-2 rounded-full border border-brand-50 bg-brand-50/40 px-3 py-1.5 text-sm font-medium text-forest" data-testid={`referral-code-${c.code}`}>
                <button onClick={() => copy(c.code)} className="inline-flex items-center gap-2" title="Copy code">
                  <span className="font-mono">{c.code}{c.is_master ? " ★" : ""}</span>
                  <span className="text-[10px] text-muted2">· {c.uses || 0} used</span>
                  <Copy size={13} className="opacity-60 group-hover:opacity-100" />
                </button>
                {!c.is_master && (
                  <button onClick={() => deleteCode(c.code)} className="text-danger/60 transition-colors hover:text-danger" title="Delete code" data-testid={`delete-referral-code-${c.code}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-brand-50 bg-white shadow-card">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-brand-50 text-left text-muted2"><th className="p-4">ID</th><th className="p-4">{t("common.name")}</th><th className="p-4">{t("auth.email")}</th><th className="p-4">{t("common.status")}</th><th className="p-4">Sponsor</th><th className="p-4">KYC</th><th className="p-4">Joined</th><th className="p-4">Action</th></tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-brand-50/60" data-testid={`admin-user-${u.distributor_id}`}>
                <td className="p-4 font-medium text-forest">{u.distributor_id}</td>
                <td className="p-4 text-ink">{u.full_name}</td>
                <td className="p-4 text-muted2">{u.email}</td>
                <td className="p-4"><div className="flex items-center gap-1.5"><StatusBadge status={u.status} size="sm" />{u.blocked && <span className="rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-semibold text-danger" data-testid={`blocked-badge-${u.distributor_id}`}>Blocked</span>}</div></td>
                <td className="p-4 text-muted2">{u.sponsor_id || "—"}</td>
                <td className="p-4 capitalize text-muted2">{(u.kyc_status || "").replace("_", " ")}</td>
                <td className="p-4 text-muted2">{(u.created_at || "").slice(0, 10)}</td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openEdit(u)} disabled={busyId === u.id} className="inline-flex items-center gap-1.5 rounded-full border border-forest/25 px-3 py-1.5 text-xs font-semibold text-forest transition-all hover:bg-brand-50 disabled:opacity-50" data-testid={`edit-user-${u.distributor_id}`}>
                      <Pencil size={13} /> Edit
                    </button>
                    <button onClick={() => resetPw(u)} disabled={busyId === u.id} className="inline-flex items-center gap-1.5 rounded-full border border-forest/25 px-3 py-1.5 text-xs font-semibold text-forest transition-all hover:bg-brand-50 disabled:opacity-50" data-testid={`reset-pw-${u.distributor_id}`}>
                      <KeyRound size={13} /> {busyId === u.id ? "…" : "Reset Password"}
                    </button>
                    <button onClick={() => openCredit(u)} disabled={busyId === u.id} className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1.5 text-xs font-semibold text-gold transition-all hover:bg-gold-light/50 disabled:opacity-50" data-testid={`credit-user-${u.distributor_id}`}>
                      <Wallet size={13} /> Credit ₹
                    </button>
                    <button onClick={() => toggleBlock(u)} disabled={busyId === u.id} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 ${u.blocked ? "border-forest/25 text-forest hover:bg-brand-50" : "border-gold/40 text-gold hover:bg-gold-light/50"}`} data-testid={`block-toggle-${u.distributor_id}`}>
                      <Ban size={13} /> {u.blocked ? "Unblock" : "Block"}
                    </button>
                    <button onClick={() => deleteUser(u)} disabled={busyId === u.id} className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger transition-all hover:bg-danger/10 disabled:opacity-50" data-testid={`delete-user-${u.distributor_id}`}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="edit-user-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-medium text-ink">Edit profile — {editUser.distributor_id}</h3>
              <button onClick={() => setEditUser(null)} className="text-muted2 hover:text-ink" data-testid="edit-user-close"><X size={18} /></button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[["full_name", "Full Name"], ["email", "Email"], ["mobile", "Mobile"], ["pincode", "Pincode"], ["state", "State"], ["city", "City"]].map(([k, label]) => (
                <label key={k} className="text-sm text-ink">
                  {label}
                  <input value={editForm[k] ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-brand-50 px-3 focus:outline-none focus:ring-2 focus:ring-brand" data-testid={`edit-field-${k}`} />
                </label>
              ))}
              <label className="text-sm text-ink sm:col-span-2">
                Address
                <input value={editForm.address ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-brand-50 px-3 focus:outline-none focus:ring-2 focus:ring-brand" data-testid="edit-field-address" />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditUser(null)} className="rounded-full border border-brand-50 px-4 py-2 text-sm font-semibold text-muted2 hover:bg-brand-50/40" data-testid="edit-user-cancel">Cancel</button>
              <button onClick={saveEdit} disabled={busyId === editUser.id} className="rounded-full bg-forest px-5 py-2 text-sm font-semibold text-white hover:bg-brand disabled:opacity-50" data-testid="edit-user-save">{busyId === editUser.id ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      {creditUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="credit-user-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-medium text-ink">Manual Wallet Credit</h3>
              <button onClick={() => setCreditUser(null)} className="text-muted2 hover:text-ink" data-testid="credit-user-close"><X size={18} /></button>
            </div>
            <p className="mt-1 text-xs text-muted2">Credit <b className="text-forest">{creditUser.distributor_id}</b> — {creditUser.full_name}. Adds to available balance (MANUAL_ADMIN_CREDIT).</p>
            <label className="mt-4 block text-sm text-ink">Amount (₹)
              <input type="number" min="1" value={creditForm.amount} onChange={(e) => setCreditForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-brand-50 px-3 focus:outline-none focus:ring-2 focus:ring-brand" data-testid="credit-amount" />
            </label>
            <label className="mt-3 block text-sm text-ink">Reason (optional)
              <input value={creditForm.reason} onChange={(e) => setCreditForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Manual admin credit" className="mt-1 h-10 w-full rounded-lg border border-brand-50 px-3 focus:outline-none focus:ring-2 focus:ring-brand" data-testid="credit-reason" />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setCreditUser(null)} className="rounded-full border border-brand-50 px-4 py-2 text-sm font-semibold text-muted2 hover:bg-brand-50/40" data-testid="credit-cancel">Cancel</button>
              <button onClick={submitCredit} disabled={busyId === creditUser.id} className="rounded-full bg-forest px-5 py-2 text-sm font-semibold text-white hover:bg-brand disabled:opacity-50" data-testid="credit-submit">{busyId === creditUser.id ? "Crediting…" : "Credit"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
